/**
 * Sign in — 1:1 with the design. Four states: password, enrollment, code required, and the single refusal.
 *
 * The refusal is the load-bearing part: wrong password, wrong code, unknown email and a disabled account
 * all render the SAME message. The form never confirms that an email exists, so it cannot be used to
 * enumerate staff. Do not "improve" this by telling the user which half was wrong.
 */
import { useState, type FormEvent } from "react";
import { api, ApiError } from "../lib/api";
import { useSession } from "../lib/session";
import { Alert, C, Warn } from "../ui/primitives";
import { QrCode, groupSecret } from "../ui/qr";

const Mark = () => (
  <svg viewBox="0 0 120 120" width="18" height="18" fill="none">
    <rect x="22" y="12" width="20" height="96" rx="10" fill="#F5F3EE" />
    <path fillRule="evenodd" fill="#F5F3EE" d="M28,76 a32,32 0 1,0 64,0 a32,32 0 1,0 -64,0 Z M46,76 a14,14 0 1,0 28,0 a14,14 0 1,0 -28,0 Z" />
  </svg>
);

type Stage = "password" | "code" | "enroll";

export function SignIn({ expired }: { expired?: boolean }) {
  const { signIn } = useSession();
  const [stage, setStage] = useState<Stage>("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [enroll, setEnroll] = useState<{ secret: string; uri: string } | null>(null);
  const [refused, setRefused] = useState(false);
  const [busy, setBusy] = useState(false);
  const [offline, setOffline] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setRefused(false); setOffline(false);
    try {
      if (stage === "enroll") {
        const r = await api.confirmEnrollment(email, password, code);
        signIn(r.token, r.user);
        return;
      }
      const r = await api.signIn(email, password, stage === "code" ? code : undefined);
      if (r.status === "ok") { signIn(r.token, r.user); return; }
      if (r.status === "enroll") { setEnroll({ secret: r.secret, uri: r.uri }); setStage("enroll"); return; }
      setStage("code");
    } catch (err) {
      // A network failure is not a refusal — saying "invalid credentials" when the server is unreachable
      // sends an operator hunting for a password problem that does not exist.
      if (err instanceof ApiError && err.code === "network") setOffline(true);
      else { setRefused(true); setCode(""); }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "26px 18px" }}>
      <div style={{ width: "100%", maxWidth: 430, background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 18, padding: "26px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: 10, background: "#1C2030", display: "flex", alignItems: "center", justifyContent: "center" }}><Mark /></div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 15 }}>Payment Manager</div>
            <div style={{ fontSize: 11, color: "var(--muted)", fontWeight: 700 }}>Bluepilo staff console</div>
          </div>
        </div>

        {expired && (
          <div style={{ display: "flex", gap: 9, alignItems: "flex-start", padding: "11px 13px", border: `1px solid ${C.warnLine}`, background: C.warnSoft, borderRadius: 11 }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={C.warn} strokeWidth="2.2" strokeLinecap="round" style={{ flex: "none", marginTop: 1 }}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 3" /></svg>
            <span style={{ fontSize: 12.5, lineHeight: 1.55, color: "var(--ink2)" }}>
              <b>Your session ended.</b> Sessions slide at 30 minutes of inactivity and hard-stop at 12 hours. Sign in to continue where you left off.
            </span>
          </div>
        )}

        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 13 }}>
          {stage === "password" && (
            <>
              <label style={lbl}><span style={lblText}>Work email</span>
                <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required autoFocus autoComplete="username" placeholder="you@bluepilo.com" style={fld} /></label>
              <label style={lbl}><span style={lblText}>Password</span>
                <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" required autoComplete="current-password" placeholder="••••••••••••" style={fld} /></label>
            </>
          )}

          {stage === "enroll" && enroll && (
            <>
              <div>
                <div style={{ fontWeight: 800, fontSize: 15 }}>Set up your authenticator</div>
                <div style={{ fontSize: 12.5, color: "var(--ink2)", lineHeight: 1.55, marginTop: 4 }}>
                  First sign-in. Scan this with Google Authenticator, 1Password or any TOTP app, then enter the
                  code it shows. <b>No session is issued until a live code is confirmed.</b>
                </div>
              </div>
              <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
                <div style={{ width: 120, height: 120, flex: "none", border: "1px solid var(--line2)", borderRadius: 12, background: "var(--subtle)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <QrCode value={enroll.uri} size={108} />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
                  <span style={{ fontSize: 11, fontWeight: 800, color: "var(--muted)", letterSpacing: ".05em" }}>CAN'T SCAN? ENTER THE SECRET</span>
                  <span className="bp-mono" style={{ fontSize: 12.5, fontWeight: 600, wordBreak: "break-all", background: "var(--subtle)", border: "1px solid var(--line2)", borderRadius: 8, padding: "8px 10px" }}>{groupSecret(enroll.secret)}</span>
                  {/* On the phone itself there is no camera to point at the screen — the link opens the app directly. */}
                  <a href={enroll.uri} style={{ fontSize: 11.5, fontWeight: 800 }}>On this device? Open your authenticator →</a>
                </div>
              </div>
            </>
          )}

          {(stage === "code" || stage === "enroll") && (
            <label style={lbl}>
              <span style={lblText}>{stage === "enroll" ? "Code from your app" : "Enter your code"}</span>
              <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                inputMode="numeric" autoComplete="one-time-code" required autoFocus placeholder="000000" className="bp-mono"
                style={{ ...fld, fontSize: 19, letterSpacing: ".22em", textAlign: "center" }} />
            </label>
          )}

          {refused && (
            <div style={{ display: "flex", gap: 9, alignItems: "flex-start", padding: "11px 13px", border: `1px solid ${C.dangerLine}`, background: C.dangerSoft, borderRadius: 11 }}>
              <Alert />
              <span style={{ fontSize: 12.5, lineHeight: 1.5, color: "var(--ink2)" }}>
                <b>We couldn't sign you in with those details.</b> Check them and try again, or ask a super admin to check your account.
              </span>
            </div>
          )}
          {offline && (
            <div style={{ display: "flex", gap: 9, alignItems: "flex-start", padding: "11px 13px", border: `1px solid ${C.warnLine}`, background: C.warnSoft, borderRadius: 11 }}>
              <Warn size={15} />
              <span style={{ fontSize: 12.5, lineHeight: 1.5, color: "var(--ink2)" }}>
                <b>Couldn't reach the payments engine.</b> Your details were not the problem — nothing was sent. Try again in a moment.
              </span>
            </div>
          )}

          <button type="submit" disabled={busy} style={{ padding: 13, border: "none", borderRadius: 11, background: busy ? "var(--muted)" : C.accent, color: "#fff", fontSize: 13.5, fontWeight: 800, cursor: busy ? "wait" : "pointer" }}>
            {busy ? "Checking…" : stage === "password" ? "Continue" : stage === "enroll" ? "Confirm code and sign in" : "Sign in"}
          </button>

          <div style={{ fontSize: 11, color: "var(--muted)", fontWeight: 600, lineHeight: 1.5, textAlign: stage === "password" ? "left" : "center" }}>
            {stage === "password"
              ? "You'll be asked for a 6-digit code from your authenticator app next. There is no way in without it."
              : "Codes rotate every 30 seconds. No remember-this-device."}
          </div>
        </form>
      </div>
    </div>
  );
}

const lbl: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 5 };
const lblText: React.CSSProperties = { fontSize: 12, fontWeight: 700 };
const fld: React.CSSProperties = { padding: "12px 13px", border: "1px solid var(--line2)", borderRadius: 10, background: "var(--subtle)", color: "var(--ink)", fontSize: 14 };
